import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MoreVertical, ArrowLeft, Camera as CameraIcon } from 'lucide-react';
import { getAllCameras, deleteCamera } from '../../../../api/index';
// import AddCameraButton from './components/AddCameraButton';
import '../../../../../src/views/Stores/Insight/evidencePage/video.css';
import CameraThumbnail from '../../../../assets/images/KPI/possessed-photography-jtcNLmMqIUE-unsplash 1 (1).png';

// const CyanCameraGlyph = ({ size = 18 }) => (
//   <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
//     <path
//       d="M13.1437 11.2557L11.5125 10.3182L14.1375 8.36825L15.75 9.30575L13.1437 11.2557ZM3 15.0057V13.5057H6.75V9.04325L4.5 7.7495C4.1375 7.537 3.90325 7.234 3.79725 6.8405C3.69125 6.447 3.74425 6.06875 3.95625 5.70575L5.08125 3.75575C5.29375 3.39325 5.597 3.159 5.991 3.053C6.385 2.947 6.763 3 7.125 3.212L14.2687 7.337L9.69375 10.7495L8.25 9.9245V13.5057C8.25 13.9183 8.103 14.2715 7.809 14.5655C7.515 14.8595 7.162 15.0062 6.75 15.0057H3Z"
//       fill="#36C5F0"
//     />
//   </svg>
// );

const Badge = ({ children }) => (
  <span
    style={{
      minWidth: 18,
      height: 16,
      padding: '0 6px',
      borderRadius: 8,
      background: '#EF4444',
      color: 'white',
      fontSize: 10,
      fontWeight: 700,
      lineHeight: '16px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 6px rgba(239,68,68,0.35)',
      transform: 'translateY(-1px)'
    }}
  >
    {children}
  </span>
);

// Device name mapping function
const getDeviceDisplayName = (deviceName) => {
  if (!deviceName) return 'Camera';
  
  const device = deviceName.toString().toUpperCase();
  
  switch (device) {
    case 'D03':
      return 'Dock Camera';
    case 'D04':
      return 'Safety Camera';
    case 'D014':
      return 'Exit Camera';
    case 'D012':
      return 'Entry Camera';
    case 'D017':
      return 'Packing Station';
    default:
      return deviceName; // Return original name if no mapping found
  }
};

const StatusPill = ({ active, rightBadge }) => (
  <div
    style={{
      display: 'flex',
      width: 40,
      height: 20,
      padding: '0 8px',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8.431,
      flexShrink: 0,
      borderRadius: 37.941,
      border: active ? '0.5px solid #81FF43' : '0.5px solid #EF4444',
      background: active ? 'rgba(129, 255, 67, 0.14)' : 'rgba(239, 68, 68, 0.14)'
    }}
  >
    <span
      style={{
        color: active ? '#81FF43' : '#EF4444',
        fontSize: 11,
        lineHeight: '16px',
        fontWeight: 700,
        letterSpacing: '0.5px'
      }}
    >
      {active ? 'ON' : 'OFF'}
    </span>
    {rightBadge != null && rightBadge !== '' && <Badge>{rightBadge}</Badge>}
  </div>
);

const LazyImage = ({ src, alt, style, onError, fallbackElement, placeholder }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          setIsLoading(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px' // Increased for better UX
      }
    );

    observerRef.current = observer;

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setIsLoading(false);
  }, []);

  const handleError = useCallback(
    (e) => {
      setHasError(true);
      setIsLoading(false);
      if (onError) {
        onError(e);
      }
    },
    [onError]
  );

  const renderPlaceholder = () => {
    if (placeholder) {
      return placeholder;
    }
    return (
      <div
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 100%)',
          color: 'rgba(255,255,255,0.4)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            animation: 'shimmer 1.5s infinite'
          }}
        />
        <CameraIcon size={25} color="rgba(255,255,255,0.4)" />
      </div>
    );
  };

  const renderLoadingSpinner = () => (
    <div
      style={{
        ...style,
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(2px)',
        zIndex: 1
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          border: '2px solid rgba(54, 197, 240, 0.2)',
          borderTop: '2px solid #36C5F0',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}
      />
    </div>
  );

  return (
    <div ref={imgRef} style={{ ...style, position: 'relative' }}>
      {!isInView ? (
        renderPlaceholder()
      ) : hasError && fallbackElement ? (
        fallbackElement
      ) : (
        <>
          {isLoading && renderLoadingSpinner()}
          <img
            src={src}
            alt={alt}
            style={{
              ...style,
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.4s ease-in-out',
              display: hasError ? 'none' : 'block'
            }}
            onLoad={handleLoad}
            onError={handleError}
            loading="lazy"
          />
        </>
      )}
    </div>
  );
};

const NeoCameraCard = ({ camera, onSelect, isSelected, showEvidenceList, isExpanded, onToggleExpand, preFetchedEvidenceCount }) => {
  const dispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [evidenceData, setEvidenceData] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceFilter, setEvidenceFilter] = useState(camera?.device_name === 'D017' ? 'packing' : 'dock');
  const [thumbnails, setThumbnails] = useState({});
  const [processingThumbnails, setProcessingThumbnails] = useState(new Set());
  const [generatedThumbnails, setGeneratedThumbnails] = useState(new Set());

  const videoPlaying = useSelector((state) => state.cameraReducer.videoPlaying);
  const selectedDate = useSelector((state) => state.customization.currentDate);

  const zones = useMemo(() => Object.keys(camera?.zones || {}), [camera]);

  // Responsive breakpoints
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = viewportWidth < 640;
  // const isTablet = viewportWidth >= 640 && viewportWidth < 1024;
  // const isSmallLaptop = viewportWidth >= 1024 && viewportWidth < 1366;
  // const isMediumLaptop = viewportWidth >= 1366 && viewportWidth < 1600;
  // const isLargeLaptop = viewportWidth >= 1600;

  // Calculate responsive container width
  // const getContainerWidth = () => {
  //   if (isMobile) return { minWidth: 280, maxWidth: 320 };
  //   if (isTablet) return { minWidth: 300, maxWidth: 350 };
  //   if (isSmallLaptop) return { minWidth: 320, maxWidth: 380 };
  //   if (isMediumLaptop) return { minWidth: 350, maxWidth: 450 };
  //   if (isLargeLaptop) return { minWidth: 400, maxWidth: 500 };
  //   return { minWidth: 350, maxWidth: 450 }; // default
  // };

  // const containerWidth = getContainerWidth();

  // Normalize camera active status from various API shapes
  const isActive = useMemo(() => {
    if (typeof camera?.active === 'boolean') return camera.active;
    if (typeof camera?.isActive === 'boolean') return camera.isActive;
    const s = camera?.status;
    if (typeof s === 'boolean') return s;
    if (typeof s === 'string') {
      const val = s.trim().toLowerCase();
      return val === 'on' || val === 'active' || val === 'online' || val === 'true' || val === '1';
    }
    return false;
  }, [camera]);

  // Reset evidence filter when camera changes
  useEffect(() => {
    if (camera?.device_name === 'D017') {
      setEvidenceFilter('packing');
    } else if (camera?.device_name === 'D04') {
      setEvidenceFilter('safety');
    } else if (camera?.device_name === 'D012' || camera?.device_name === 'D014') {
      setEvidenceFilter('truck');
    } else {
      setEvidenceFilter('dock');
    }
  }, [camera?.device_name]);

  // Fetch evidence data when card is expanded and when camera/date changes
  useEffect(() => {
    if (selectedDate && camera?.device_name && isExpanded) {
      fetchEvidenceData();
    }
  }, [selectedDate, camera?.device_name, isExpanded]);

  const fetchEvidenceData = async () => {
    try {
      setEvidenceLoading(true);
      setThumbnails({});
      setProcessingThumbnails(new Set());
      setGeneratedThumbnails(new Set());

      const response = await fetch('https://db.neophyte.live/fm-api/kpiCreation/evidenceList', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDate,
          device: camera.device_name
        })
      });

      if (response.ok) {
        const data = await response.json();
        setEvidenceData(data || []);
      }
    } catch (error) {
      console.error('Error fetching evidence data:', error);
    } finally {
      setEvidenceLoading(false);
    }
  };

  // Generate thumbnail for a specific video
  const generateThumbnail = useCallback(
    (videoPath, index) => {
      if (generatedThumbnails.has(index) || processingThumbnails.has(index) || thumbnails[index]) {
        return;
      }

      setProcessingThumbnails((prev) => new Set([...prev, index]));

      const video = document.createElement('video');
      video.src = videoPath;
      // Remove crossOrigin to avoid CORS issues with Azure blob storage
      video.preload = 'metadata';
      video.muted = true;

      const cleanup = () => {
        setProcessingThumbnails((prev) => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
        setGeneratedThumbnails((prev) => new Set([...prev, index]));
        if (video.timeoutId) clearTimeout(video.timeoutId);
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        video.src = '';
      };

      const onLoadedMetadata = () => {
        video.currentTime = Math.min(2, video.duration * 0.1);
      };

      const onSeeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 640;
          canvas.height = 360;
          const ctx = canvas.getContext('2d');

          // Try to draw video frame, fallback to placeholder if CORS error
          try {
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
            setThumbnails((prev) => ({ ...prev, [index]: thumbnailUrl }));
            console.log(`Thumbnail generated successfully for index ${index}`);
          } catch (corsError) {
            console.log(`CORS error for index ${index}, creating placeholder:`, corsError.message);
            // Create placeholder thumbnail
            const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            gradient.addColorStop(0, '#2a3359');
            gradient.addColorStop(1, '#1f2745');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Add play icon
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const size = 50;
            ctx.moveTo(centerX - size / 2, centerY - size / 2);
            ctx.lineTo(centerX + size / 2, centerY);
            ctx.lineTo(centerX - size / 2, centerY + size / 2);
            ctx.closePath();
            ctx.fill();

            // Add text
            ctx.font = 'bold 16px Arial';
            ctx.textAlign = 'center';
            ctx.fillStyle = '#ffffff';
            ctx.fillText('Evidence Video', centerX, centerY + size + 25);

            const placeholderUrl = canvas.toDataURL('image/jpeg', 0.8);
            setThumbnails((prev) => ({ ...prev, [index]: placeholderUrl }));
          }
        } catch (e) {
          console.error(`Thumbnail generation error for index ${index}:`, e);
          setThumbnails((prev) => ({ ...prev, [index]: null }));
        } finally {
          cleanup();
        }
      };

      const onError = () => {
        console.log(`Video load error for index ${index}, creating placeholder thumbnail`);
        // Create placeholder thumbnail when video fails to load
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 640;
          canvas.height = 360;
          const ctx = canvas.getContext('2d');

          // Create gradient background
          const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
          gradient.addColorStop(0, '#2a3359');
          gradient.addColorStop(1, '#1f2745');
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Add play icon
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          const centerX = canvas.width / 2;
          const centerY = canvas.height / 2;
          const size = 50;
          ctx.moveTo(centerX - size / 2, centerY - size / 2);
          ctx.lineTo(centerX + size / 2, centerY);
          ctx.lineTo(centerX - size / 2, centerY + size / 2);
          ctx.closePath();
          ctx.fill();

          // Add text
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.fillStyle = '#ffffff';
          ctx.fillText('Evidence Video', centerX, centerY + size + 25);

          const placeholderUrl = canvas.toDataURL('image/jpeg', 0.8);
          setThumbnails((prev) => ({ ...prev, [index]: placeholderUrl }));
        } catch (canvasError) {
          console.error(`Canvas error for index ${index}:`, canvasError);
          setThumbnails((prev) => ({ ...prev, [index]: null }));
        }
        cleanup();
      };

      video.addEventListener('loadedmetadata', onLoadedMetadata);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', onError);

      // Fail-safe timeout
      video.timeoutId = setTimeout(() => {
        setThumbnails((prev) => ({ ...prev, [index]: null }));
        cleanup();
      }, 15000);
    },
    [thumbnails, processingThumbnails, generatedThumbnails]
  );

  const handleFilterChange = (filter) => setEvidenceFilter(filter);

  // Check camera types
  const isD03Camera = camera?.device_name === 'D03'; // Has both dock and safety
  const isD04Camera = camera?.device_name === 'D04'; // Only safety
  const isD012Camera = camera?.device_name === 'D012'; // Only truck
  const isD014Camera = camera?.device_name === 'D014'; // Only truck
  const isD017Camera = camera?.device_name === 'D017'; // Has packing type

  // Filter evidence data based on selected filter
  const filteredEvidenceData = useMemo(() => {
    if (!evidenceData.length) return [];

    if (isD04Camera) return evidenceData.filter((item) => item.type === 'safety');
    if (isD012Camera || isD014Camera) return evidenceData.filter((item) => item.type === 'truck');
    if (isD017Camera) return evidenceData.filter((item) => item.type === 'packing');

    if (evidenceFilter === 'safety') return evidenceData.filter((item) => item.type === 'safety');
    if (evidenceFilter === 'dock') return evidenceData.filter((item) => item.type === 'dock');
    if (evidenceFilter === 'packing') return evidenceData.filter((item) => item.type === 'packing');

    return evidenceData;
  }, [evidenceData, evidenceFilter, isD04Camera, isD012Camera, isD014Camera, isD017Camera]);

  // Enhanced lazy loading with intersection observer
  const evidenceObserverRef = useRef(null);

  useEffect(() => {
    if (!filteredEvidenceData.length) return;

    // Disconnect previous observer
    if (evidenceObserverRef.current) {
      evidenceObserverRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index, 10);
            const evidence = filteredEvidenceData[index];

            // Only generate video thumbnail if no image_url is available
            if (
              evidence?.videos &&
              !evidence?.image_url &&
              !generatedThumbnails.has(index) &&
              !processingThumbnails.has(index) &&
              !thumbnails[index]
            ) {
              // Add small delay to prevent overwhelming the browser
              setTimeout(() => {
                generateThumbnail(evidence.videos, index);
              }, index * 100); // Stagger thumbnail generation
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '150px' // Increased for better preloading
      }
    );

    evidenceObserverRef.current = observer;

    // Use a more specific selector and add error handling
    const evidenceItems = document.querySelectorAll('[data-evidence-item]');
    evidenceItems.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => {
      if (evidenceObserverRef.current) {
        evidenceObserverRef.current.disconnect();
      }
    };
  }, [filteredEvidenceData, generateThumbnail, thumbnails, processingThumbnails, generatedThumbnails]);

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Get short type name
  const getShortType = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'dock':
        return 'Dock';
      case 'safety':
        return 'Safety';
      case 'truck':
        return 'Truck';
      case 'packing':
        return 'Packing';
      default:
        return type || 'Unknown';
    }
  };

  // Helper function to capitalize text
  const capitalizeText = (text) => {
    if (!text) return text;
    return text
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get status badge content based on camera type and evidence data
  const getStatusBadge = (evidence) => {
    if (isD012Camera || isD014Camera || isD017Camera) {
      // D012, D014, and D017: render nothing
      return null;
    }

    if (isD03Camera) {
      // D03: if type is safety, show PPE detection status
      if (evidence?.type?.toLowerCase() === 'safety') {
        const ppeStatus = [];
        if (evidence?.shoes === true) ppeStatus.push('Shoes');
        if (evidence?.helmet === true) ppeStatus.push('Helmet');
        if (evidence?.ppe === true) ppeStatus.push('PPE');

        if (ppeStatus.length > 0) {
          return ppeStatus.join(', ');
        }

        return 'No PPE';
      }

      // D03: for other types, show instance count or similar metric
      return capitalizeText(evidence?.instance || evidence?.count || 'Instance');
    }

    if (isD04Camera) {
      // D04: show PPE detection status (shoes, helmet, ppe)
      const ppeStatus = [];
      if (evidence?.shoes === true) ppeStatus.push('Shoes');
      if (evidence?.helmet === true) ppeStatus.push('Helmet');
      if (evidence?.ppe === true) ppeStatus.push('PPE');

      if (ppeStatus.length > 0) {
        return ppeStatus.join(', ');
      }

      return 'No PPE';
    }

    return capitalizeText('Unloaded Helmet');
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      await deleteCamera(camera._id);
      dispatch({ type: 'CAMERA_ADDED' });
      dispatch({ type: 'VIDEO_PLAYING', videoPlaying: false });
      setSnackbar({ open: true, message: 'Camera deleted successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || 'Failed to delete camera', severity: 'error' });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleCardClick = () => {
    if (!camera?.active) return;

    if (isExpanded) {
      onToggleExpand?.(null);
    } else {
      onToggleExpand?.(camera._id);
      dispatch({ type: 'CAMERA_DATA', cameraData: camera });
      onSelect?.(camera);
      if (selectedDate && camera?.device_name && evidenceData.length === 0) {
        fetchEvidenceData();
      }
    }
  };

  const evidenceCount = isExpanded
    ? filteredEvidenceData.length
    : evidenceData.length > 0
    ? evidenceData.length
    : preFetchedEvidenceCount ?? camera?.evidenceCount ?? camera?.eventsCount ?? 0;

  return (
    <>
      <div
        className="neo-camera-card"
        onClick={handleCardClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          width: '100%',
          maxWidth: '100%',
          padding: isMobile ? '16px' : '20px',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          alignSelf: 'stretch',
          borderRadius: 15,
          background: 'linear-gradient(100deg, rgba(88, 96, 136, 0.43) 25.96%, rgba(54, 197, 240, 0.17) 94.4%)',
          cursor: camera?.active ? 'pointer' : 'not-allowed',
          opacity: camera?.active ? 1 : 0.75,
          border: isExpanded
            ? '2px solid #36C5F0'
            : isSelected && !showEvidenceList
            ? '2px solid #36C5F0'
            : '1px solid rgba(255,255,255,0.08)',
          position: 'relative',
          transition: 'all 0.25s ease',
          boxShadow: isExpanded || (isSelected && !showEvidenceList) ? '0 0 18px rgba(54,197,240,0.28)' : 'none',
          marginBottom: 8,
          marginTop: 2,
          boxSizing: 'border-box'
        }}
      >
        {/* Top row */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 16,
            width: '100%'
          }}
        >
          {/* Camera thumbnail with lazy loading */}
          <div
            style={{
              width: isMobile ? 100 : 120,
              height: isMobile ? 100 : 120,
              flexShrink: 0,
              borderRadius: 15,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: isMobile ? 8 : 12,
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #2a3359 0%, #1f2745 100%)'
            }}
          >
            <LazyImage
              src={CameraThumbnail}
              alt={camera?.device_name ? `${camera.device_name} thumbnail` : 'Camera thumbnail'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 15
              }}
              placeholder={
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #2a3359 0%, #1f2745 100%)',
                    color: 'rgba(255,255,255,0.5)'
                  }}
                >
                  {/* <CyanCameraGlyph size={30} /> */}
                </div>
              }
            />
          </div>

          {/* Right content */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              color: 'white',
              paddingLeft: isMobile ? 0 : '8px',
              paddingTop: isMobile ? 12 : 0,
              paddingRight: isMobile ? 24 : 28,
              position: 'relative',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <div style={{ color: '#FFF', fontFamily: 'Inter', fontSize: isMobile ? 18 : 20, fontWeight: 600, lineHeight: '28px' }}>
                {getDeviceDisplayName(camera?.device_name)}
              </div>
              <StatusPill active={isActive} />
            </div>

            {/* Three dot - positioned at the extreme right of the card header */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu((s) => !s);
              }}
              style={{
                position: 'absolute',
                top: 10,
                right: 0,
                background: 'transparent',
                border: 'none',
                borderRadius: 4,
                padding: 4,
                color: 'rgba(255,255,255,0.85)',
                cursor: 'pointer',
                display: 'flex'
                // alignItems: 'center',
                // justifyContent: 'center'
              }}
              aria-label="Open menu"
            >
              <MoreVertical size={16} />
            </button>

            {/* Location tags */}
            <div
              className="zones-row"
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 8,
                marginTop: '16px',
                width: '100%',
                overflowX: 'hidden'
              }}
            >
              {zones.slice(0, 2).map((z) => (
                <span
                  key={z}
                  style={{
                    display: 'flex',
                    width: 'auto',
                    height: 28,
                    padding: '0 12px',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    borderRadius: 40.471,
                    border: '0.6px solid #56577A',
                    background: 'linear-gradient(176deg, #1B2242 32.43%, #060C28 99.85%)',
                    color: '#FFF',
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: 13,
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '28px',
                    letterSpacing: -0.1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {z}
                </span>
              ))}
              {/* Uncomment if you want to show the "+N" badge for extra zones */}
              {/* {zones.length > 2 && (
    <span
      style={{
        background: 'linear-gradient(180deg, rgba(12,18,44,0.9), rgba(12,18,44,0.6))',
        color: 'white',
        padding: '6px 12px',
        borderRadius: 14,
        fontSize: 12,
        border: '1px solid rgba(255,255,255,0.12)',
        whiteSpace: 'nowrap',
      }}
    >
      +{zones.length - 2}
    </span>
  )} */}
            </div>

            {/* Evidence count badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
              <div
                style={{
                  width: isMobile ? 34 : 40,
                  height: isMobile ? 34 : 40,
                  borderRadius: 20,
                  background: 'rgba(113,142,191,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '16px'
                }}
                aria-hidden
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.08898 1.27093C9.23185 1.12809 9.42561 1.04785 9.62764 1.04785C9.82967 1.04785 10.0234 1.12809 10.1663 1.27093L11.2833 2.38788C11.4261 2.53079 11.5064 2.72461 11.5063 2.92668C11.5063 3.12876 11.426 3.32254 11.2831 3.4654C11.1402 3.60827 10.9463 3.68851 10.7443 3.68847C10.5422 3.68843 10.3484 3.60813 10.2055 3.46521L9.08859 2.34826C8.94576 2.20538 8.86552 2.01162 8.86552 1.80959C8.86552 1.60756 8.94614 1.41381 9.08898 1.27093ZM9.99907 18.7612C8.42345 18.7612 7.24593 18.7014 6.46421 18.642C6.0086 18.6103 5.5798 18.4149 5.25686 18.092C4.93391 17.7691 4.73858 17.3403 4.70688 16.8846C4.64745 16.1029 4.58764 14.9254 4.58764 13.3494C4.58764 11.7734 4.64745 10.5959 4.70688 9.81417C4.73869 9.3585 4.93417 8.92969 5.25726 8.60681C5.58036 8.28393 6.00929 8.08873 6.46498 8.05721C7.24669 7.99778 8.42421 7.93797 9.99983 7.93797C11.5755 7.93797 12.753 7.99778 13.5347 8.05721C13.9903 8.08892 14.4191 8.28424 14.742 8.60719C15.065 8.93014 15.2603 9.35893 15.292 9.81455C15.3515 10.5963 15.4113 11.7738 15.4113 13.3498C15.4113 14.9258 15.3515 16.1033 15.292 16.885C15.2603 17.3406 15.065 17.7694 14.742 18.0924C14.4191 18.4153 13.9903 18.6107 13.5347 18.6424C12.753 18.7018 11.5755 18.7616 9.99983 18.7616M12.9956 15.6789C12.9956 15.881 12.9154 16.0748 12.7725 16.2177C12.6296 16.3606 12.4358 16.4408 12.2337 16.4408H9.99983C9.79776 16.4408 9.60397 16.3606 9.46109 16.2177C9.3182 16.0748 9.23793 15.881 9.23793 15.6789C9.23793 15.4769 9.3182 15.2831 9.46109 15.1402C9.60397 14.9973 9.79776 14.917 9.99983 14.917H12.2337C12.4358 14.917 12.6296 14.9973 12.7725 15.1402C12.9154 15.2831 12.9956 15.4769 12.9956 15.6789ZM12.2337 14.1132C12.4358 14.1132 12.6296 14.0329 12.7725 13.8901C12.9154 13.7472 12.9956 13.5534 12.9956 13.3513C12.9956 13.1492 12.9154 12.9554 12.7725 12.8126C12.6296 12.6697 12.4358 12.5894 12.2337 12.5894H10.7442C10.5421 12.5894 10.3484 12.6697 10.2055 12.8126C10.0626 12.9554 9.98231 13.1492 9.98231 13.3513C9.98231 13.5534 10.0626 13.7472 10.2055 13.8901C10.3484 14.0329 10.5421 14.1132 10.7442 14.1132H12.2337ZM15.0071 2.3875C14.9363 2.31671 14.8523 2.26055 14.7599 2.22222C14.6675 2.18389 14.5684 2.16414 14.4683 2.16411C14.3682 2.16407 14.2691 2.18375 14.1766 2.22201C14.0842 2.26028 14.0001 2.31638 13.9294 2.38712C13.8586 2.45786 13.8024 2.54185 13.7641 2.63429C13.7257 2.72673 13.706 2.82582 13.706 2.9259C13.7059 3.02597 13.7256 3.12507 13.7639 3.21754C13.8021 3.31001 13.8582 3.39404 13.929 3.46483L15.0459 4.58178C15.1167 4.65255 15.2007 4.70869 15.2931 4.74699C15.3855 4.7853 15.4846 4.80502 15.5847 4.80504C15.6847 4.80506 15.7838 4.78537 15.8762 4.7471C15.9687 4.70882 16.0527 4.65271 16.1235 4.58197C16.1942 4.51124 16.2504 4.42725 16.2887 4.33482C16.327 4.24238 16.3467 4.14331 16.3467 4.04325C16.3467 3.94319 16.327 3.84411 16.2888 3.75167C16.2505 3.65922 16.1944 3.57521 16.1236 3.50445L15.0071 2.3875ZM4.24898 2.3875C4.31973 2.31672 4.40373 2.26057 4.49619 2.22226C4.58865 2.18395 4.68775 2.16423 4.78783 2.16423C4.88791 2.16423 4.98701 2.18395 5.07947 2.22226C5.17193 2.26057 5.25594 2.31672 5.32669 2.3875L6.44364 3.50445C6.58651 3.64736 6.66675 3.84118 6.66671 4.04325C6.66667 4.24533 6.58637 4.43911 6.44345 4.58197C6.30054 4.72484 6.10673 4.80508 5.90465 4.80504C5.70258 4.80501 5.50879 4.7247 5.36593 4.58178L4.2486 3.46445C4.17781 3.3937 4.12166 3.30969 4.08335 3.21723C4.04505 3.12477 4.02533 3.02567 4.02533 2.92559C4.02533 2.82551 4.04505 2.72641 4.08335 2.63395C4.12166 2.5415 4.17781 2.45749 4.2486 2.38674M10.0002 4.77036C6.75298 4.77036 4.5705 5.5475 3.1785 6.36502C2.48517 6.77188 1.9964 7.18483 1.67374 7.50597C1.51298 7.66636 1.39374 7.8035 1.31183 7.90597C1.26859 7.95942 1.22717 8.0143 1.18764 8.07055L1.1785 8.08426L1.17469 8.08959L1.17317 8.09188L1.17279 8.09302C1.17279 8.09302 1.17202 8.09378 1.79679 8.50293L1.17202 8.09378C1.06184 8.26322 1.02349 8.46948 1.06539 8.6672C1.10729 8.86492 1.22602 9.03789 1.39545 9.14807C1.56489 9.25825 1.77115 9.29661 1.96887 9.25471C2.16659 9.2128 2.33956 9.09408 2.44974 8.92464C2.45837 8.9127 2.47488 8.89137 2.49926 8.86064C2.54802 8.79969 2.62993 8.70445 2.74955 8.58559C2.98764 8.34826 3.37621 8.01607 3.95069 7.67893C5.09355 7.00693 7.00707 6.29417 10.0002 6.29417C12.9934 6.29417 14.9065 7.00693 16.0497 7.67855C16.6242 8.01607 17.0124 8.34826 17.2509 8.58521C17.3392 8.67242 17.4228 8.76437 17.5012 8.86064C17.5255 8.89112 17.5421 8.91245 17.5507 8.92464L17.5572 8.93417C17.6123 9.01764 17.6834 9.08942 17.7663 9.14542C17.8493 9.20142 17.9424 9.24054 18.0405 9.26054C18.1385 9.28055 18.2395 9.28105 18.3377 9.26201C18.436 9.24297 18.5295 9.20477 18.613 9.14959C18.6964 9.09441 18.7682 9.02333 18.8242 8.94041C18.8802 8.85749 18.9194 8.76435 18.9394 8.66631C18.9594 8.56826 18.9599 8.46724 18.9408 8.36901C18.9218 8.27078 18.8836 8.17726 18.8284 8.09378L18.1907 8.51131C18.8284 8.09378 18.8276 8.09302 18.8276 8.09302L18.8269 8.09188L18.8254 8.08959L18.8215 8.08426L18.8124 8.07055L18.7842 8.03055C18.7534 7.98808 18.7215 7.94641 18.6886 7.90559C18.5752 7.76578 18.4543 7.63224 18.3263 7.50559C18.004 7.18483 17.5149 6.77188 16.8215 6.36502C15.4299 5.54712 13.2475 4.77036 10.0002 4.77036Z"
                    fill="#718EBF"
                  />
                </svg>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ color: 'white', fontSize: isMobile ? 18 : 22, fontWeight: 700, marginTop: '16px' }}>{evidenceCount}</span>
                <span style={{ color: '#9CA3AF', fontSize: isMobile ? 10 : 12, fontWeight: 600, marginTop: '16px' }}>Evidences</span>
              </div>
            </div>
          </div>
        </div>

        {/* Menu */}
        {showMenu && (
          <div
            style={{
              position: 'absolute',
              top: 40,
              right: 8,
              background: 'rgba(25,32,69,0.97)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 10,
              zIndex: 20,
              minWidth: 140,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowMenu(false)}
              style={menuBtnStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Edit
            </button>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)' }} />
            <button
              onClick={() => {
                if (videoPlaying) dispatch({ type: 'VIDEO_PLAYING', videoPlaying: false });
                setShowDeleteModal(true);
                setShowMenu(false);
              }}
              style={{ ...menuBtnStyle, color: '#EF4444' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239,68,68,0.12)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Delete
            </button>
          </div>
        )}

        {/* Evidence List (expanded) */}
        {isExpanded && (
          <div
            style={{
              height: 349,
              alignSelf: 'stretch',
              borderRadius: 15
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 16,
                color: 'white'
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'white' }}>Evidences</h4>
                {/* <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#9CA3AF' }}>
                  All evidences ({evidenceCount}) -{' '}
                  {isD04Camera
                    ? 'Safety'
                    : isD012Camera || isD014Camera
                    ? 'Truck'
                    : isD017Camera
                    ? 'Packing'
                    : evidenceFilter.charAt(0).toUpperCase() + evidenceFilter.slice(1)}
                </p> */}
              </div>

              {/* Segmented Filter (only for D03) */}
              {isD03Camera && (
                <div
                  role="tablist"
                  aria-label="Evidence type"
                  style={{
                    display: 'inline-flex',
                    padding: '3px 3px 3px 12px',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    borderRadius: 32,
                    background: 'linear-gradient(176deg, rgba(27, 34, 66, 0.59) 32.43%, rgba(6, 12, 40, 0.59) 99.85%)',
                    gap: 6
                  }}
                >
                  {(isD03Camera ? ['safety', 'dock'] : ['dock']).map((key) => {
                    const active = evidenceFilter === key;
                    return (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFilterChange(key);
                        }}
                        role="tab"
                        aria-selected={active}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 999,
                          border: 'none',
                          cursor: 'pointer',
                          background: active ? 'rgba(54, 197, 240, 0.25)' : 'transparent',
                          color: active ? '#36C5F0' : '#A0AEC0',
                          fontFamily: 'Plus Jakarta Sans',
                          fontSize: 9,
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: '12.616px'
                        }}
                      >
                        {key === 'safety' ? 'Safety' : key === 'dock' ? 'Dock' : key === 'packing' ? 'Packing' : key}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Evidence grid */}
            {evidenceLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220, color: 'white' }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    border: '2px solid rgba(54, 197, 240, 0.3)',
                    borderTop: '2px solid #36C5F0',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
              </div>
            ) : filteredEvidenceData.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220, color: '#9CA3AF', fontSize: 12 }}>
                No {isD017Camera ? 'packing' : evidenceFilter} evidence found
              </div>
            ) : (
              <div
                className="evidence-scroll"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12,
                  width: '100%',
                  height: 280,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  paddingBottom: 4,
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {filteredEvidenceData.map((evidence, index) => (
                  <div
                    key={`evidence-${camera?.device_name}-${index}-${evidence.start_time || index}`}
                    data-evidence-item
                    data-index={index}
                    style={{
                      width: 280,
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12
                    }}
                  >
                    {/* Thumbnail Section */}
                    <div
                      style={{
                        position: 'relative',
                        width: '100%',
                        height: 157,
                        borderRadius: 16,
                        overflow: 'hidden',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (evidence?.videos) {
                          console.log('Clicking evidence video, setting source:', evidence.videos);
                          // Use the direct blob URL without proxy prefix
                          dispatch({ type: 'SET_CAMERA_SOURCE', videoSource: evidence.videos });
                        }
                      }}
                    >
                      {evidence?.image_url ? (
                        <LazyImage
                          src={evidence.image_url}
                          alt={`Evidence thumbnail ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 16
                          }}
                          fallbackElement={
                            thumbnails[index] ? (
                              <LazyImage
                                src={thumbnails[index]}
                                alt={`Generated evidence thumbnail ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: 16
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'linear-gradient(135deg, rgba(42, 51, 89, 0.8) 0%, rgba(31, 39, 69, 0.8) 100%)',
                                  color: 'rgba(255,255,255,0.6)',
                                  fontSize: 12,
                                  textAlign: 'center',
                                  borderRadius: 16,
                                  border: '1px dashed rgba(255,255,255,0.2)'
                                }}
                              >
                                <div style={{ textAlign: 'center' }}>
                                  <CameraIcon size={32} color="rgba(255,255,255,0.4)" />
                                  <div style={{ marginTop: 8, fontSize: 10 }}>Loading...</div>
                                </div>
                              </div>
                            )
                          }
                          placeholder={
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, rgba(42, 51, 89, 0.6) 0%, rgba(31, 39, 69, 0.6) 100%)',
                                color: 'rgba(255,255,255,0.4)',
                                borderRadius: 16,
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: '-100%',
                                  width: '100%',
                                  height: '100%',
                                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                                  animation: 'shimmer 2s infinite'
                                }}
                              />
                              <CameraIcon size={28} color="rgba(255,255,255,0.4)" />
                            </div>
                          }
                        />
                      ) : thumbnails[index] ? (
                        <LazyImage
                          src={thumbnails[index]}
                          alt={`Generated evidence thumbnail ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 16
                          }}
                        />
                      ) : processingThumbnails.has(index) ? (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, rgba(42, 51, 89, 0.8) 0%, rgba(31, 39, 69, 0.8) 100%)',
                            color: 'rgba(255,255,255,0.6)',
                            borderRadius: 16,
                            position: 'relative'
                          }}
                        >
                          <div style={{ textAlign: 'center' }}>
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                border: '2px solid rgba(54, 197, 240, 0.3)',
                                borderTop: '2px solid #36C5F0',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 8px'
                              }}
                            />
                            <div style={{ fontSize: 10 }}>Generating...</div>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, rgba(42, 51, 89, 0.6) 0%, rgba(31, 39, 69, 0.6) 100%)',
                            color: 'rgba(255,255,255,0.4)',
                            borderRadius: 16,
                            border: '1px dashed rgba(255,255,255,0.2)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                              animation: 'shimmer 2s infinite'
                            }}
                          />
                          <div style={{ textAlign: 'center', zIndex: 1 }}>
                            <CameraIcon size={28} color="rgba(255,255,255,0.4)" />
                            <div style={{ marginTop: 8, fontSize: 10 }}>Evidence Video</div>
                          </div>
                        </div>
                      )}

                      {/* Type Badge */}
                      <span
                        style={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          borderRadius: '27.101px',
                          border: '0.301px solid #060C29',
                          background: '#0F123B',
                          color: '#FFF',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <div
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: '#FFF'
                          }}
                        />
                        {getShortType(evidence?.type)}
                      </span>
                    </div>

                    {/* Content Section - Outside of any card */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8
                      }}
                    >
                      {/* Title and Status Badges Row */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap'
                        }}
                      >
                        {/* Title */}
                        <h3
                          style={{
                            margin: 0,
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#FFF',
                            fontFamily: '"Plus Jakarta Sans"',
                            fontStyle: 'normal',
                            lineHeight: '140%'
                          }}
                        >
                          {evidence?.title || `Evidence - ${index + 1}`}
                        </h3>

                        {/* Status Badges - Don't show for safety evidence as we have separate PPE badges, and don't show for packing station */}
                        {getStatusBadge(evidence, camera?.device_name) && 
                         evidence?.type?.toLowerCase() !== 'safety' && 
                         !isD017Camera && (
                          <span
                            style={{
                              background: 'rgba(54, 197, 240, 0.2)',
                              color: '#36C5F0',
                              padding: '4px 8px',
                              borderRadius: 12,
                              fontSize: 10,
                              fontWeight: 600
                            }}
                          >
                            {getStatusBadge(evidence, camera?.device_name)}
                          </span>
                        )}

                        {/* PPE Badges for Safety Evidence */}
                        {(isD03Camera && evidence?.type?.toLowerCase() === 'safety') || isD04Camera ? (
                          <>
                            {evidence?.shoes === true && (
                              <span
                                style={{
                                  background: 'transparent',
                                  color: '#EF4444',
                                  border: '1px solid #EF4444',
                                  padding: '4px 8px',
                                  borderRadius: 12,
                                  fontSize: 9,
                                  fontWeight: 600
                                }}
                              >
                                Shoes
                              </span>
                            )}
                            {evidence?.helmet === true && (
                              <span
                                style={{
                                  background: 'transparent',
                                  color: '#EF4444',
                                  border: '1px solid #EF4444',
                                  padding: '4px 8px',
                                  borderRadius: 12,
                                  fontSize: 9,
                                  fontWeight: 600
                                }}
                              >
                                Helmet
                              </span>
                            )}
                            {evidence?.ppe === true && (
                              <span
                                style={{
                                  background: 'transparent',
                                  color: '#EF4444',
                                  border: '1px solid #EF4444',
                                  padding: '4px 8px',
                                  borderRadius: 12,
                                  fontSize: 9,
                                  fontWeight: 600
                                }}
                              >
                                PPE
                              </span>
                            )}
                          </>
                        ) : null}
                      </div>

                      {/* Timestamp */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: 8,
                        background: 'rgba(54, 197, 240, 0.1)',
                        border: '1px solid rgba(54, 197, 240, 0.3)',
                        borderRadius: 8,
                        padding: '6px 10px',
                        marginTop: 4
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="#36C5F0" strokeWidth="2"/>
                          <polyline points="12,6 12,12 16,14" stroke="#36C5F0" strokeWidth="2"/>
                        </svg>
                        <span style={{ 
                          fontSize: 11, 
                          color: '#36C5F0', 
                          fontWeight: 600,
                          fontFamily: 'Plus Jakarta Sans',
                          textAlign: 'center'
                        }}>
                          {evidence?.start_time ? formatDate(evidence.start_time) : 'No timestamp'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => !isDeleting && setShowDeleteModal(false)}
        >
          <div
            style={{
              background: 'linear-gradient(145deg, #1B2242, #060C28)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 15,
              padding: 24,
              maxWidth: 420,
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: 'white', margin: '0 0 12px 0', fontSize: 18 }}>Confirm Deletion</h3>
            <p style={{ color: '#9CA3AF', margin: '0 0 22px 0', lineHeight: 1.55 }}>
              Delete “{camera?.device_name}”? This will also remove its zones.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 10,
                  color: '#B7C0D1',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  background: '#EF4444',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  cursor: 'not-allowed' && isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {isDeleting && (
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      border: '2px solid rgba(255,255,255,0.35)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                )}
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <div
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: snackbar.severity === 'success' ? 'rgba(16,185,129,0.95)' : 'rgba(239,68,68,0.95)',
            color: 'white',
            padding: '12px 18px',
            borderRadius: 10,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <span>{snackbar.message}</span>
          <button
            onClick={() => setSnackbar((s) => ({ ...s, open: false }))}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 6
            }}
            aria-label="Close"
          >
            x
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        /* Grid scrollbar */
        .evidence-grid::-webkit-scrollbar {
          width: 6px;
        }
        .evidence-grid::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 3px;
        }
        .evidence-grid::-webkit-scrollbar-thumb {
          background: rgba(54, 197, 240, 0.5);
          border-radius: 3px;
        }
        .evidence-grid::-webkit-scrollbar-thumb:hover {
          background: rgba(54, 197, 240, 0.7);
        }
        /* Evidence scroll */
        .evidence-scroll::-webkit-scrollbar {
          height: 6px;
        }
        .evidence-scroll::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 3px;
        }
        .evidence-scroll::-webkit-scrollbar-thumb {
          background: rgba(54, 197, 240, 0.5);
          border-radius: 3px;
        }
        .evidence-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(54, 197, 240, 0.7);
        }
      `}</style>
    </>
  );
};

const menuBtnStyle = {
  width: '100%',
  padding: '12px 16px',
  background: 'transparent',
  border: 'none',
  color: 'white',
  textAlign: 'left',
  cursor: 'pointer',
  fontSize: 14
};

const NeoRightBar = () => {
  const dispatch = useDispatch();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCameraId, setExpandedCameraId] = useState(null);
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const cameraAdded = useSelector((state) => state.cameraReducer.cameraAdded);
  const selectedCamera = useSelector((state) => state.cameraReducer.cameraData);
  const selectedDate = useSelector((state) => state.customization.currentDate);

  // Responsive breakpoints for main container
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = viewportWidth < 640;
  const isTablet = viewportWidth >= 640 && viewportWidth < 1024;
  const isSmallLaptop = viewportWidth >= 1024 && viewportWidth < 1440;
  const is1440Screen = viewportWidth >= 1440 && viewportWidth < 1600;
  const isLargeLaptop = viewportWidth >= 1600;

  // Calculate responsive container width
  const getMainContainerWidth = () => {
    if (isMobile) return { minWidth: 280, maxWidth: 320 };
    if (isTablet) return { minWidth: 300, maxWidth: 350 };
    if (isSmallLaptop) return { minWidth: 300, maxWidth: 340 };
    if (is1440Screen) return { minWidth: 360, maxWidth: 420 };
    if (isLargeLaptop) return { minWidth: 420, maxWidth: 480 };
    return { minWidth: 320, maxWidth: 380 }; // default
  };

  const mainContainerWidth = getMainContainerWidth();

  const fetchCameras = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await getAllCameras();
      const arr = resp?.cameras || [];
      setCameras(arr);

      if (selectedDate && arr.length > 0) {
        fetchEvidenceForAllCameras(arr);
      }
    } catch (e) {
      setError(e?.message || 'Failed to fetch cameras');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvidenceForAllCameras = async (cams) => {
    try {
      const evidencePromises = cams.map(async (cam) => {
        if (cam?.device_name) {
          const response = await fetch('https://db.neophyte.live/fm-api/kpiCreation/evidenceList', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ date: selectedDate, device: cam.device_name })
          });
          if (response.ok) {
            const data = await response.json();
            return { cameraId: cam._id, evidenceCount: data?.length || 0 };
          }
        }
        return { cameraId: cam._id, evidenceCount: 0 };
      });

      const results = await Promise.all(evidencePromises);
      const countsMap = {};
      results.forEach((r) => (countsMap[r.cameraId] = r.evidenceCount));
      setEvidenceCounts(countsMap);
    } catch (error) {
      console.error('Error fetching evidence for all cameras:', error);
    }
  };

  useEffect(() => {
    fetchCameras();
    if (cameraAdded) dispatch({ type: 'RESET_CAMERA_ADDED' });
  }, [cameraAdded, dispatch]);

  useEffect(() => {
    if (selectedDate && cameras.length > 0) {
      fetchEvidenceForAllCameras(cameras);
    }
  }, [selectedDate, cameras]);

  const handleCameraSelect = (cam) => {
    if (selectedDate && cam?.device_name) setExpandedCameraId(cam._id);
  };

  const handleToggleExpand = (cameraId) => setExpandedCameraId(cameraId);

  useEffect(() => {
    if (selectedDate) {
      dispatch({ type: 'CAMERA_DATA', cameraData: {} });
      setExpandedCameraId(null);
    }
  }, [selectedDate, dispatch]);

  const isCameraSelected = (cam) => selectedCamera && selectedCamera._id === cam._id;

  return (
    <>
      {/* Mobile trigger */}
      <div className="relative lg:mt-0 mt-[200%]">
        <button
          className="absolute top-2 right-2 z-50 md:hidden"
          onClick={() => setIsDrawerOpen(true)}
          style={{
            background: 'rgba(0,0,0,0.3)',
            border: 'none',
            borderRadius: 6,
            padding: 8,
            color: 'black',
            cursor: 'pointer'
          }}
          aria-label="Open cameras panel"
        >
          <ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} />
        </button>
      </div>

      {/* Mobile Drawer */}
      {isDrawerOpen && (
        <div
          className="md:hidden"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'block'
          }}
          onClick={() => setIsDrawerOpen(false)}
        >
          <div
            style={{
              width: 320,
              height: '100%',
              background: 'linear-gradient(110deg, rgba(6, 11, 38, 0.94) 74.67%, rgba(6, 11, 40, 0.31) 100%)',
              backdropFilter: 'blur(5.05px)',
              marginLeft: 'auto',
              overflowY: 'auto',
              padding: 20,
              borderLeft: '1px solid rgba(255,255,255,0.06)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsDrawerOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: 6,
                padding: 8,
                color: 'white',
                cursor: 'pointer',
                marginBottom: 20
              }}
              aria-label="Close"
            >
              ×
            </button>

            <div style={{ padding: 4 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'flex-start' }}>
                {loading ? (
                  <LoadingBlock />
                ) : error ? (
                  <ErrorBlock message={error} />
                ) : cameras.length === 0 ? (
                  <EmptyBlock />
                ) : (
                  cameras
                    .slice()
                    .sort((a, b) => (b.active === a.active ? 0 : b.active ? 1 : -1))
                    .map((cam) => (
                      <NeoCameraCard
                        key={cam._id}
                        camera={cam}
                        onSelect={handleCameraSelect}
                        isSelected={isCameraSelected(cam)}
                        showEvidenceList={false}
                        isExpanded={expandedCameraId === cam._id}
                        onToggleExpand={handleToggleExpand}
                        preFetchedEvidenceCount={evidenceCounts[cam._id]}
                      />
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Panel */}
      <div
        className="hidden md:block neo-cameras-container"
        style={{
          display: 'flex',
          padding: '2px 16px 20px 16px',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: 20,
          borderRadius: 16,
          background: 'linear-gradient(110deg, rgba(6, 11, 38, 0.94) 74.67%, rgba(6, 11, 40, 0.31) 100%)',
          backdropFilter: 'blur(5.050000190734863px)',
          WebkitBackdropFilter: 'blur(5.050000190734863px)',
          maxHeight: '100vh',
          minHeight: 600,
          height: 'auto',
          minWidth: mainContainerWidth.minWidth,
          maxWidth: mainContainerWidth.maxWidth,
          width: '100%',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.08)',
          position: 'relative',
          boxSizing: 'border-box'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingBottom: 15,
            paddingLeft: 0,
            paddingRight: 0
          }}
        >
          <h1 style={{ color: 'white', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.5)', margin: 0, fontSize: 24 }}>Cameras</h1>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            width: '100%',
            maxHeight: 600,
            overflowY: 'auto',
            paddingRight: 0,
            justifyContent: 'flex-start'
          }}
        >
          {loading ? (
            <LoadingBlock withIconColor />
          ) : error ? (
            <ErrorBlock message={error} />
          ) : cameras.length === 0 ? (
            <EmptyBlock />
          ) : (
            cameras
              .slice()
              .sort((a, b) => (b.active === a.active ? 0 : b.active ? 1 : -1))
              .map((cam) => (
                <NeoCameraCard
                  key={cam._id}
                  camera={cam}
                  onSelect={handleCameraSelect}
                  isSelected={isCameraSelected(cam)}
                  showEvidenceList={false}
                  isExpanded={expandedCameraId === cam._id}
                  onToggleExpand={handleToggleExpand}
                  preFetchedEvidenceCount={evidenceCounts[cam._id]}
                />
              ))
          )}
        </div>
      </div>

      <style>{`
        .zones-row::-webkit-scrollbar {
          display: none;
        }
        .zones-row {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }
        .neo-cameras-container::-webkit-scrollbar {
          width: 6px;
        }
        .neo-cameras-container::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 3px;
        }
        .neo-cameras-container::-webkit-scrollbar-thumb {
          background: rgba(54, 197, 240, 0.5);
          border-radius: 3px;
        }
        .neo-cameras-container::-webkit-scrollbar-thumb:hover {
          background: rgba(54, 197, 240, 0.7);
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

const LoadingBlock = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: 'white' }}>
    <div
      style={{
        width: 40,
        height: 40,
        border: `3px solid rgba(54, 197, 240, 0.3)`,
        borderTop: '3px solid #36C5F0',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
  </div>
);

const ErrorBlock = ({ message }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
    <p style={{ color: '#EF4444', margin: 0 }}>{message}</p>
  </div>
);

const EmptyBlock = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      height: 200,
      gap: 12,
      paddingTop: 20
    }}
  >
    <CameraIcon size={48} color="#6B7280" />
    <p style={{ color: '#9CA3AF', margin: 0 }}>No cameras found</p>
  </div>
);

export default NeoRightBar;
